import { SendIcon } from "@/assets/images/svgIcons/sendIcon"
import { Button } from "@/components/ui/baseUi/button"
import { Skeleton } from "@/components/ui/baseUi/skeleton"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { UserCell } from "@/components/ui/layout/table/userCell"
import { BoxShopSalesEntity } from "@/modules/boxShop/entities/boxShopSalesEntity"
import type { BoxShopSendEmailFormValues } from "@/modules/boxShop/form/boxShopFormik"
import { useBoxShopSalesSendEmail } from "@/modules/boxShop/hooks/useBoxShopSales"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { useUserProfile } from "@/modules/profile/publicProfile/hooks/useUserProfile"
import { LoaderCircleIcon } from "lucide-react"
import { z } from "zod"

type BoxShopSendEmailProps = {
  saleId: string
  userId: string | null
  classNames?: string
} & React.ComponentProps<"div">

export function BoxShopSendEmail({ saleId, userId, classNames, ...props }: BoxShopSendEmailProps) {
  const { data: user, isLoading } = useUserProfile(userId ?? "")
  const { sendEmail, isSendEmailLoading } = useBoxShopSalesSendEmail()

  const validationSchema = z.object({
    userId: z.string().nullable(),
    email: z
      .string()
      .nullable()
      .refine((value) => value !== null && value.length > 0, { message: word("global.form.required") })
      .refine((value) => value === null || z.email().safeParse(value).success, {
        message: word("global.input.email.error"),
      }),
  })

  const form = useAppForm({
    defaultValues: {
      userId: userId,
      email: null as string | null,
    } satisfies BoxShopSendEmailFormValues,
    validators: {
      onChange: validationSchema,
      onSubmit: validationSchema,
    },
    onSubmit: async ({ value }) => {
      await sendEmail({
        saleId: saleId,
        input: BoxShopSalesEntity.fromFormikToMailDto(saleId, value),
      })
        .then(() => {
          toast.success(word("boxShop.sendEmail.toast.success"), {
            classNames: successToastClassName(),
          })
        })
        .catch(() => {
          toast.error(word("boxShop.sendEmail.toast.error"), {
            classNames: errorToastClassName(),
          })
        })
    },
  })

  if (isLoading) return <Skeleton className="h-17 w-full" />

  if (!user)
    return (
      <div className={cn("flex flex-col gap-2", classNames)} {...props}>
        <form.AppField name="email">
          {(field) => (
            <field.Field>
              <field.Label required>{word("global.form.label.email")}</field.Label>
              <field.Content>
                <div className="flex flex-row items-center gap-4">
                  <field.Input
                    className="flex-1 bg-interactive-background-neutral-default"
                    type="email"
                    placeholder={word("global.form.placeholder.email")}
                    value={field.state.value ?? ""}
                    disabled={isSendEmailLoading}
                  />
                  <form.Subscribe selector={(state) => state.canSubmit}>
                    {(canSubmit) => (
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <Button
                              variant="secondarySecondary"
                              size="iconLg"
                              onClick={() => {
                                void form.handleSubmit()
                              }}
                              disabled={isSendEmailLoading || !canSubmit}
                            >
                              {isSendEmailLoading ? (
                                <LoaderCircleIcon className="animate-spin" />
                              ) : (
                                <SendIcon color="currentColor" className="text-textDefault-body" />
                              )}
                            </Button>
                          }
                        />
                        <TooltipContent side="bottom">
                          {word("boxShop.sendEmail.tooltip.sendPaymentOrder")}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </form.Subscribe>
                </div>
                <field.Message />
              </field.Content>
            </field.Field>
          )}
        </form.AppField>
      </div>
    )

  return (
    <div className="flex flex-row items-center justify-between">
      <UserCell
        member={{
          avatar: user.avatar ?? undefined,
          firstname: user.firstname ?? "",
          lastname: user.lastname ?? "",
        }}
        avatarSize="size-12"
        subtitle={user.username === "" ? "" : "@" + user.username}
      />
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="secondarySecondary"
              size="iconLg"
              onClick={() => {
                void sendEmail({
                  saleId: saleId,
                  input: BoxShopSalesEntity.fromFormikToMailDto(saleId, {
                    userId: userId,
                    email: form.state.values.email,
                  }),
                })
                  .then(() => {
                    toast.success(word("boxShop.sendEmail.toast.success"), {
                      classNames: successToastClassName(),
                    })
                  })
                  .catch(() => {
                    toast.error(word("boxShop.sendEmail.toast.error"), {
                      classNames: errorToastClassName(),
                    })
                  })
              }}
              disabled={isSendEmailLoading}
            >
              {isSendEmailLoading ? (
                <LoaderCircleIcon className="animate-spin" />
              ) : (
                <SendIcon color="currentColor" className="text-textDefault-body" />
              )}
            </Button>
          }
        />
        <TooltipContent side="bottom">{word("boxShop.sendEmail.tooltip.sendPaymentOrder")}</TooltipContent>
      </Tooltip>
    </div>
  )
}
